import { UNGROUPED_LABELS, LOW_CONFIDENCE_THRESHOLD } from './photorealisticConstants';

export function getInteriorNumericConfidence(item) {
  let c = Number(item?.confidence ?? 0);
  if (Number.isNaN(c)) return 1;
  if (c > 1 && c <= 100) c = c / 100;
  return c;
}

export function isLowConfidenceInterior(item) {
  return getInteriorNumericConfidence(item) < LOW_CONFIDENCE_THRESHOLD;
}

/** Stable key for matching the same interior across list updates when possible. */
export function getInteriorStableKey(item, indexInArray) {
  const id = item?.id ?? item?.interior_id;
  if (id != null && id !== '') return `id:${id}`;
  return `idx:${indexInArray}`;
}

/**
 * Resolve display label for an interior item. GET /interiors returns interior_type_id but no label;
 * match with supplier interior types (id → interiorType). Also supports item.interiorType (object with interiorType string).
 * Falls back to item.label then "Object N".
 */
export function getLabelForInterior(item, interiorTypesList, index) {
  if (item?.label != null && String(item.label).trim() !== '') return String(item.label).trim();
  const nestedName = item?.interiorType?.interiorType ?? item?.interior_type?.interiorType;
  if (nestedName != null && String(nestedName).trim() !== '') return String(nestedName).trim();
  const typeId = item?.interior_type_id ?? item?.interiorTypeId;
  if (typeId != null && Array.isArray(interiorTypesList)) {
    const t = interiorTypesList.find((x) => x.id === Number(typeId));
    if (t?.interiorType != null) return String(t.interiorType).trim();
  }
  return `Object ${(index ?? 0) + 1}`;
}

/** Shown when user tries to delete structural interiors (walls, floors, ceilings). */
export const INTERIOR_TYPE_DELETION_BLOCKED_MESSAGE = 'Cannot delete this type of objects.';

/**
 * True for resolved labels that match structural room geometry (wall/floor/ceiling),
 * which must not be deleted from the list or targeted by remove-object.
 */
export function isInteriorTypeDeletionBlocked(item, interiorTypesList, index) {
  const norm = getLabelForInterior(item, interiorTypesList, index).trim().toLowerCase();
  return UNGROUPED_LABELS.has(norm);
}

/**
 * Group polygons for the object list: Wall/Floor/Ceiling stay as single rows;
 * all other labels are grouped (same label → one dropdown).
 */
export function groupObjectList(polygons, interiorTypesList = []) {
  if (!polygons?.length) return [];
  const singles = [];
  const byLabel = new Map();
  polygons.forEach((item, i) => {
    const label = getLabelForInterior(item, interiorTypesList, i).trim();
    const norm = label.toLowerCase();
    if (UNGROUPED_LABELS.has(norm)) {
      singles.push({ type: 'single', label, indices: [i], items: [item], firstIndex: i });
    } else {
      if (!byLabel.has(label)) byLabel.set(label, { type: 'group', label, indices: [], items: [], firstIndex: i });
      const g = byLabel.get(label);
      g.indices.push(i);
      g.items.push(item);
    }
  });
  const groups = [...singles, ...byLabel.values()];
  groups.sort((a, b) => (a.firstIndex ?? a.indices[0]) - (b.firstIndex ?? b.indices[0]));
  return groups;
}

/**
 * Group items by label for the "Show detected objects" popup: one row per unique label.
 */
export function groupPopupListByLabel(items, interiorTypesList) {
  if (!items?.length) return [];
  const byLabel = new Map();
  items.forEach((item, i) => {
    const label = getLabelForInterior(item, interiorTypesList, i).trim() || `Object ${i + 1}`;
    if (!byLabel.has(label)) byLabel.set(label, []);
    byLabel.get(label).push(i);
  });
  return Array.from(byLabel.entries()).map(([label, indices]) => ({ label, indices }));
}

/** Find a saved interior that matches this item (by interior_type_id + bbox) and return its id for room_interior_id. */
export function getInteriorIdFromSaved(item, savedList) {
  if (!Array.isArray(savedList) || savedList.length === 0) return null;
  const typeId = item?.interior_type_id ?? item?.interiorTypeId;
  const bbox = item?.bbox;
  const match = savedList.find((s) => {
    if ((s.interior_type_id ?? s.interiorTypeId) !== typeId) return false;
    const sb = s.bbox;
    if (!Array.isArray(bbox) || !Array.isArray(sb) || bbox.length !== sb.length) return false;
    return bbox.every((v, i) => Number(v) === Number(sb[i]));
  });
  return match?.id ?? match?.interior_id ?? null;
}

/** Checkbox indices for popup lists: items already on the canvas detection list (`polygons`). */
export function buildPreselectedSetsFromPolygons(polygons, detectedList, userDefinedList) {
  const detectedSet = new Set();
  const userSet = new Set();
  if (!Array.isArray(polygons) || polygons.length === 0) {
    return { detectedSet, userSet };
  }
  const byId = new Set();
  const byTypeBbox = [];
  for (const p of polygons) {
    const id = p?.id ?? p?.interior_id;
    if (id != null && id !== '') {
      byId.add(Number(id));
    } else {
      const typeId = p?.interior_type_id ?? p?.interiorTypeId;
      const bbox = p?.bbox;
      if (typeId != null && Array.isArray(bbox)) {
        byTypeBbox.push({ typeId, bbox });
      }
    }
  }
  const bboxMatches = (a, b) =>
    Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => Number(v) === Number(b[i]));

  const matchItem = (item) => {
    const id = item?.id ?? item?.interior_id;
    if (id != null && id !== '' && byId.has(Number(id))) return true;
    const typeId = item?.interior_type_id ?? item?.interiorTypeId;
    const bbox = item?.bbox;
    return byTypeBbox.some((x) => x.typeId === typeId && bboxMatches(x.bbox, bbox));
  };

  if (Array.isArray(detectedList)) {
    detectedList.forEach((item, i) => {
      if (matchItem(item)) detectedSet.add(i);
    });
  }
  if (Array.isArray(userDefinedList)) {
    userDefinedList.forEach((item, i) => {
      if (matchItem(item)) userSet.add(i);
    });
  }
  return { detectedSet, userSet };
}
